
import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PencilIcon, ImageIcon, AlertCircle, BookOpen } from "lucide-react";
import { toast } from 'sonner';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import GalleryImageGrid from './image-overlay/GalleryImageGrid';
import ImageSelector from './image-overlay/ImageSelector';
import ImageErrorFallback from './image-overlay/ImageErrorFallback';
import ImageLoadingIndicator from './image-overlay/ImageLoadingIndicator';

interface ImageOverlayProps {
  imageUrl: string;
  alt?: string;
  className?: string;
  imageClassName?: string;
  aspectRatio?: string;
  editButtonPosition?: "top-right" | "bottom-right" | "top-left" | "bottom-left";
  onImageChange: (newUrl: string) => void;
  category?: "finance" | "housing" | "loans" | "lifestyle" | "energy" | "car" | "boats" | "guide";
  useStorageBucket?: boolean;
  showGuideIcon?: boolean;
  showEditButton?: boolean;
}

const ImageOverlay: React.FC<ImageOverlayProps> = ({
  imageUrl,
  alt = "Article image",
  className = "",
  imageClassName = "", 
  aspectRatio = "",
  editButtonPosition = "top-right",
  onImageChange,
  category = "finance",
  useStorageBucket = true,
  showGuideIcon = false,
  showEditButton = true
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  // MUUTOS: Preload image in background before component mounts
  useEffect(() => {
    if (!currentImageUrl) return;
    
    // Preload image without visibility changes
    const img = new Image();
    img.src = currentImageUrl;
    
    // We'll only track errors, not success (let the img element handle that)
    img.onerror = () => {
      console.error("Failed to preload image:", currentImageUrl);
      setImageError(true);
      setIsLoading(false);
    };
    
    return () => {
      img.onerror = null;
    };
  }, [currentImageUrl]);

  // Update current URL when prop changes
  useEffect(() => {
    if (imageUrl !== currentImageUrl) {
      setCurrentImageUrl(imageUrl);
      setImageError(false);
      setIsLoading(true);
      setRetryCount(0);
    }
  }, [imageUrl]);

  // MUUTOS: Yksinkertaistettu kuvalataus niin ettei overlay näy kuin tarvittaessa
  const handleImageChange = (newImage: {id: string; src: string; alt: string; category: string}) => {
    setCurrentImageUrl(newImage.src);
    onImageChange(newImage.src);
    setDialogOpen(false);
    setImageError(false);
    setIsLoading(true);
    setRetryCount(0);
    toast.success("Kuva päivitetty onnistuneesti!");
  };

  const handleUploadComplete = (newImageUrl: string) => {
    setCurrentImageUrl(newImageUrl);
    onImageChange(newImageUrl);
    setDialogOpen(false);
    setImageError(false);
    setIsLoading(true);
    setRetryCount(0);
    toast.success("Kuva ladattu ja päivitetty onnistuneesti!");
  };

  const handleRetry = () => {
    setDialogOpen(true);
  };

  // Get button position classes based on editButtonPosition
  const getButtonPositionClasses = () => {
    switch (editButtonPosition) {
      case "bottom-right":
        return "bottom-2 right-2";
      case "top-left":
        return "top-2 left-2";
      case "bottom-left":
        return "bottom-2 left-2";
      case "top-right":
      default:
        return "top-2 right-2";
    }
  };

  // Determine appropriate aspect ratio
  const aspectRatioValue = () => {
    if (!aspectRatio) return undefined;
    
    switch (aspectRatio) {
      case "aspect-square": return 1 / 1;
      case "aspect-video": return 16 / 9;
      case "aspect-portrait": return 3 / 4;
      case "aspect-wide": return 21 / 9;
      default:
        if (aspectRatio.includes('/')) {
          const [width, height] = aspectRatio.split('/').map(Number);
          if (!isNaN(width) && !isNaN(height) && height !== 0) {
            return width / height;
          }
        }
        return undefined;
    }
  };

  // MUUTOS: Yksinkertaistettu renderöinti - ladataan kuva suoraan eikä näytetä loading-indikaattoria
  const renderImageContent = () => {
    if (imageError) {
      return <ImageErrorFallback onRetry={handleRetry} />;
    }
    
    return (
      <img 
        src={currentImageUrl} 
        alt={alt}
        className={`object-cover w-full h-full ${imageClassName} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onError={() => setImageError(true)}
        onLoad={() => setIsLoading(false)}
      />
    );
  };

  // Determine if this is a guide based on the category or showGuideIcon prop
  const isGuide = category === "guide" || showGuideIcon;

  return (
    <div 
      className={`relative overflow-hidden ${className} ${aspectRatio}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-testid="image-overlay"
    >
      {/* MUUTOS: Näytetään placeholder ensin ja sitten kuva sen päälle */}
      {aspectRatioValue() ? (
        <AspectRatio ratio={aspectRatioValue()} className="bg-gray-50">
          {isLoading && <div className="w-full h-full bg-gray-100 animate-pulse" />}
          {renderImageContent()}
        </AspectRatio>
      ) : (
        <div className="w-full h-full bg-gray-50">
          {isLoading && <div className="w-full h-full bg-gray-100 animate-pulse" />}
          {renderImageContent()}
        </div>
      )}
      
      {/* Always display the guide icon if this is a guide, regardless of hover */}
      {isGuide && (
        <div className="absolute top-0 right-0 p-2 bg-gradient-to-bl from-blue-600 to-transparent text-white rounded-bl-lg">
          <BookOpen className="h-4 w-4" />
        </div>
      )}
      
      {/* Edit button that shows on hover only if showEditButton is true */}
      {isHovering && showEditButton && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="secondary" 
              size="sm" 
              className={`absolute ${getButtonPositionClasses()} bg-white bg-opacity-80 hover:bg-opacity-100`}
            >
              <PencilIcon className="h-4 w-4 mr-1" />
              <span>Vaihda kuva</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <ImageSelector
              onUploadComplete={handleUploadComplete}
              onGalleryImageSelect={handleImageChange}
              defaultCategory={category}
              useStorageBucket={useStorageBucket}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ImageOverlay;
